import React from 'react'
import { convertPrintInchesToCentimeters } from 'components/MeasurementUnit/helper'
import ArtworkDescriptionConnector, {
  ConnectorProps as ArtworkDescriptionProps,
} from '../../../connectors/ArtworkDescription'
import {
  ACRYLIC,
  METAL,
  FRAMING_TYPE_ID_LIGHT_PEWTER,
  FRAMING_TYPE_ID_DARK_PEWTER,
  OPEN_EDITION,
  CANVAS,
  ROLLED_IN_TUBE,
  NOT_FRAMED,
  PRINT_DEPTH,
  CANVAS_PRINT_DEPTH,
  CANVAS_WRAP_FRAME_SIZE_IN_INCHES,
  CANVAS_WRAP_GAP_SIZE_IN_INCHES,
  NOT_APPLICABLE,
  BOX,
} from '../../../helpers/constants'
import styles from '../ArtworkDescription.module.scss'
import InfoTitleValue from '../components/InfoTitleValue/InfoTitleValue'

/** @see './Print.spec.js' exported for testing only */
export function getFramedCanvasWrapPrintDimensions(
  printWidthInInches: number,
  printHeightInInches: number,
  userSizePreference: string
): string {
  const widthInInches =
    Number(printWidthInInches) +
    (CANVAS_WRAP_FRAME_SIZE_IN_INCHES + CANVAS_WRAP_GAP_SIZE_IN_INCHES) * 2
  // left and right
  const heightInInches =
    Number(printHeightInInches) +
    (CANVAS_WRAP_FRAME_SIZE_IN_INCHES + CANVAS_WRAP_GAP_SIZE_IN_INCHES) * 2
  // top and bottom
  const widthInUserUnitOfLength =
    userSizePreference === 'in'
      ? widthInInches
      : parseInt(`${convertPrintInchesToCentimeters(widthInInches, userSizePreference)}`, 10)
  const heightInUserUnitOfLength =
    userSizePreference === 'in'
      ? heightInInches
      : parseInt(`${convertPrintInchesToCentimeters(heightInInches, userSizePreference)}`, 10)
  const depthInUserUnitOfLength =
    userSizePreference === 'in'
      ? CANVAS_PRINT_DEPTH
      : convertPrintInchesToCentimeters(CANVAS_PRINT_DEPTH, userSizePreference)
  return [
    widthInUserUnitOfLength,
    'W',
    'x',
    heightInUserUnitOfLength,
    'H',
    'x',
    depthInUserUnitOfLength,
    'D',
    userSizePreference,
  ].join(' ')
}

const PrintProductDetails = (props: ArtworkDescriptionProps) => {
  const {
    currentTab,
    selectedPrintProduct,
    selectedCanvasWrapColorOption,
    selectedFrameOption,
    userSizePreference,
  } = props
  const { material, height, width, depth } = selectedPrintProduct

  const isCanvasMaterial = material === CANVAS
  const isMetalOrAcrylic = [ACRYLIC, METAL].includes(material)
  const isFrameSelected = selectedFrameOption !== null
  const smallBorder = 4 // 2" mat border on both sides
  const isPaperPrint = material === 'Fine Art Paper' || material === 'Photo Paper'
  const isRolledInATube = isPaperPrint && !isFrameSelected
  const isReadyToHang = isFrameSelected || isCanvasMaterial || isMetalOrAcrylic

  const largeBorder = 6 // 3" mat border on both sides

  const isPewterFrameSelected = [
    FRAMING_TYPE_ID_LIGHT_PEWTER,
    FRAMING_TYPE_ID_DARK_PEWTER,
  ].includes(selectedFrameOption?.framingTypeId ?? 0)
  const frameBorder = isPewterFrameSelected
    ? 1.75 // 0.875" frame including both sides
    : 1.25

  // 0.625" frame including both sides
  const getFramedPaperPrintDimensions = (): string => {
    if (width < 18) {
      return `${parseInt(`${convertPrintInchesToCentimeters(width, userSizePreference)}`, 10) +
        smallBorder +
        frameBorder} W x ${parseInt(
        `${convertPrintInchesToCentimeters(height, userSizePreference)}`,
        10
      ) +
        smallBorder +
        frameBorder} H x ${convertPrintInchesToCentimeters(
        PRINT_DEPTH,
        userSizePreference
      )} D ${userSizePreference}`
    }

    return `${parseInt(`${convertPrintInchesToCentimeters(width, userSizePreference)}`, 10) +
      largeBorder +
      frameBorder} W x ${parseInt(
      `${convertPrintInchesToCentimeters(height, userSizePreference)}`,
      10
    ) +
      largeBorder +
      frameBorder} H x ${convertPrintInchesToCentimeters(
      PRINT_DEPTH,
      userSizePreference
    )} D ${userSizePreference}`
  }

  const getPrintSize = () => {
    return `${convertPrintInchesToCentimeters(
      width,
      userSizePreference
    )} W x ${convertPrintInchesToCentimeters(height, userSizePreference)} H x ${
      isCanvasMaterial
        ? convertPrintInchesToCentimeters(CANVAS_PRINT_DEPTH, userSizePreference)
        : convertPrintInchesToCentimeters(depth, userSizePreference)
    } D ${userSizePreference}`
  }

  const getPrintType = (): string => {
    switch (material) {
      case METAL:
        return 'Print, Ink on Aluminum'

      default:
        return `Print, Giclee on ${material}`
    }
  }

  if (currentTab !== OPEN_EDITION) {
    return null
  }

  return (
    <div className={styles.artworkInfoGrid}>
      <div className={styles.artworkInfoGridContainer}>
        {material && <InfoTitleValue title='Medium:' value={getPrintType()} isTwoColumns={true} />}

        <InfoTitleValue title='Rarity:' value='Open Edition' isTwoColumns={true} />
        <InfoTitleValue title='Size:' value={getPrintSize()} isTwoColumns={true} />

        {!isMetalOrAcrylic && isFrameSelected && (
          <InfoTitleValue
            title='Size with Frame:'
            value={
              isCanvasMaterial
                ? getFramedCanvasWrapPrintDimensions(width, height, userSizePreference)
                : getFramedPaperPrintDimensions()
            }
            isTwoColumns={true}
          />
        )}

        <InfoTitleValue
          title='Ready to Hang:'
          value={isReadyToHang ? 'Yes' : 'No'}
          isTwoColumns={true}
        />
        <InfoTitleValue
          title='Frame:'
          value={isMetalOrAcrylic ? NOT_APPLICABLE : selectedFrameOption?.title ?? NOT_FRAMED}
          isTwoColumns={true}
        />

        {isCanvasMaterial && (
          <InfoTitleValue
            title='Canvas Wrap:'
            value={selectedCanvasWrapColorOption?.title}
            isTwoColumns={true}
          />
        )}

        <InfoTitleValue
          title='Packaging:'
          value={isRolledInATube ? ROLLED_IN_TUBE : BOX}
          isTwoColumns={true}
        />
      </div>
    </div>
  )
}

export default ArtworkDescriptionConnector(PrintProductDetails)
